/**
 * Call transfer / forwarding — shared types + state machine.
 *
 * See docs/call-transfer.md for the full flow diagram and Chime integration notes.
 *
 * Transfers are BLIND ONLY — a TRUE forward, NOT a 3-way bridge. The transferrer
 * (B) never joins a group/room with the target. Instead:
 *
 * ```
 * B (transferrer)                 A (held / forwarded party)        C (target)
 *   │ blind_redial(target=C) ───► receives blind_redial
 *   │                              │ tears down A↔B leg
 *   │                              │ places a FRESH 1:1 call A→C ───► rings (caller ID = A)
 *   │ END (A↔B) + drop off         │ outgoing → active when C answers
 *   ▼                              ▼                                  ▼
 * completed                      connected to C                     connected to A
 * ```
 *
 * Because the new A↔C call is a normal 1:1 INVITE, the caller ID shown to C is
 * the forwarded party's identity (resolved from the INVITE payload + roster),
 * and there is no room roster — so no phantom participant and no group UI.
 *
 * (A consult / "warm" transfer mode used to exist; it has been removed in favor
 * of the simpler blind-only flow.)
 */

export type TransferMode = 'blind'

/** Local/UI phase for an in-progress blind transfer on a 1:1 call. */
export type TransferPhase =
  | 'idle'
  /** Blind transfer in flight. */
  | 'completing'
  | 'completed'
  | 'cancelled'
  | 'failed'

export type TransferSignalAction =
  /**
   * True blind transfer: tells the held party to drop the current leg and place
   * a fresh 1:1 call to the target. The transferrer drops off entirely. No room
   * / group bridge, so no phantom participant and correct caller ID.
   */
  | 'blind_redial'
  /** Migrate an active 1:1 call to group mode (add people / merge-to-call). */
  | 'upgrade_group'

export type TransferSignalPayload = {
  action: TransferSignalAction
  mode?: TransferMode
  /** The live 1:1 call being transferred. */
  parentCallId?: string
  targetUserId?: string
  targetDisplayName?: string
  transferrerId?: string
  /** Original 1:1 party being forwarded (for blind transfer invite UX). */
  originalCallerId?: string
  originalCallerDisplayName?: string
  /** Avatar of the transfer target, so the held party can render C immediately. */
  targetAvatarUrl?: string | null
  reason?: string
  /**
   * Marks an `upgrade_group` as transfer-flavored: the receiver migrates to the
   * room mechanics but keeps the 1:1 overlay (a transferRoom) instead of the
   * group participant grid.
   */
  transfer?: boolean
}

export type TransferGateSeverity = 'block' | 'warn' | 'allow'

/**
 * Configurable transfer gates — tune without code changes.
 * Frontend reads defaults from `DEFAULT_TRANSFER_ELIGIBILITY`; ops may override
 * via env or future admin settings.
 */
export type TransferEligibilityConfig = {
  /** Looping hold audio played to the party placed on hold. */
  holdMusicUrl: string
  gates: {
    /** Target already in an active voice/video session on any device. */
    targetOnActiveCall: TransferGateSeverity
    /** Target lifecycle is checked-in, tracking, or en-route to a session. */
    targetInActiveSession: TransferGateSeverity
    /** Target presence derived OFFLINE. */
    targetOffline: TransferGateSeverity
    /** Target manual activity has pauseNotifications (DND-like). */
    targetPauseNotifications: TransferGateSeverity
    /** Target availability calendar resolves to unavailable at transfer time. */
    targetCalendarUnavailable: TransferGateSeverity
    /** Per manual activity preset id (e.g. focus-time, out-sick). */
    activityPreset: Partial<Record<string, TransferGateSeverity>>
    /** Per session lifecycle state (checked-in, tracking, en-route, …). */
    sessionLifecycle: Partial<Record<string, TransferGateSeverity>>
  }
}

export const DEFAULT_TRANSFER_ELIGIBILITY: TransferEligibilityConfig = {
  /** Dedicated ambient hold loop (scripts/generate-sounds.mjs renderHoldMusic). */
  holdMusicUrl: '/sounds/hold-music.wav',
  gates: {
    targetOnActiveCall: 'warn',
    targetInActiveSession: 'warn',
    targetOffline: 'warn',
    targetPauseNotifications: 'warn',
    targetCalendarUnavailable: 'warn',
    activityPreset: {
      'focus-time': 'warn',
      'out-sick': 'warn',
      vacation: 'warn',
      'in-meeting': 'warn',
      commuting: 'allow',
      'working-remotely': 'allow',
    },
    sessionLifecycle: {
      'checked-in': 'warn',
      tracking: 'warn',
      'en-route': 'warn',
      'checkout-reminded': 'warn',
    },
  },
}

export type TransferEligibilityIssue = {
  code: string
  severity: Exclude<TransferGateSeverity, 'allow'>
  message: string
}

export type TransferEligibilityResult = {
  allowed: boolean
  issues: TransferEligibilityIssue[]
}

const BLIND_TRANSITIONS: Record<TransferPhase, TransferPhase[]> = {
  idle: ['completing'],
  completing: ['completed', 'failed'],
  completed: [],
  cancelled: [],
  failed: [],
}

/** Validate a local phase transition for the blind transfer UX. */
export function canTransitionTransferPhase(
  from: TransferPhase,
  to: TransferPhase,
): boolean {
  if (from === to) return true
  return BLIND_TRANSITIONS[from]?.includes(to) ?? false
}

/** Map an incoming TRANSFER signal action to the next local phase. */
export function nextTransferPhaseFromSignal(
  current: TransferPhase,
  action: TransferSignalAction,
): TransferPhase {
  switch (action) {
    case 'blind_redial':
      return 'completing'
    default:
      return current
  }
}

export function isTransferInProgress(phase: TransferPhase | undefined): boolean {
  return phase === 'completing'
}

export function buildTransferSignalPayload(
  action: TransferSignalAction,
  fields: Omit<TransferSignalPayload, 'action'>,
): TransferSignalPayload {
  return { action, ...fields }
}
